// Spin the Bottle geometry (table-local, floor origin, metres, +Y up).
// Cushion i sits at angle i × 30°, starting at −Z (North) and going
// clockwise seen from above (+X = East), like tableLayout.seatXZ.

import type { Chair } from '../types';

export const SEATS = 12;
export const RUG_R = 1.75;       // the rug: ~3.5 m across
export const CUSHION_R = 1.42;   // cushion centres from the middle
export const CUSHION_TOP = 0.15; // cushion top (seat height)
export const SIGN_R = 2.05;      // the little sign just off the rug
export const FOOTPRINT = 4.3;    // pick / select box (covers rug + sign)

export const cushionAngle = (i: number) => (i * 2 * Math.PI) / SEATS;

export function cushionXZ(i: number, r = CUSHION_R): [number, number] {
  const a = cushionAngle(i);
  return [Math.sin(a) * r, -Math.cos(a) * r];
}

/** Yaw that makes the sitter on cushion i face the middle (+Z forward convention). */
export function cushionYaw(i: number): number {
  const [x, z] = cushionXZ(i);
  return Math.atan2(-x, -z);
}

export const BOTTLE_CHAIRS: Chair[] = Array.from({ length: SEATS }, (_, i) => {
  const [x, z] = cushionXZ(i);
  return { x, z, yaw: cushionYaw(i), y: CUSHION_TOP };
});

/**
 * Bottle heading θ: the neck points along (sin θ, −cos θ) — the same angle
 * as cushionAngle, so θ = cushionAngle(i) points at cushion i. The bottle
 * model's neck is along local −Z, so its rotation.y is −θ.
 */
export const bottleRotationY = (theta: number) => -theta;

/** Ease-out quartic: fast start, long gentle settle. */
export const easeOut = (t: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 4);

/**
 * The heading a spin ends on: `turns` whole turns past `from`, landing
 * exactly on cushion `to`.
 */
export function spinEnd(from: number, to: number, turns: number): number {
  const target = cushionAngle(to);
  const TAU = Math.PI * 2;
  const delta = (((target - from) % TAU) + TAU) % TAU;
  return from + delta + Math.max(1, turns) * TAU;
}
